/**
 * Mobile BD — Business Development hub with 5 tabs:
 * Pipeline · Contacts · Companies · Consultants · Leads
 *
 * Reuses existing backend CRM endpoints — no new backend required.
 * Email validation reuses frontend/src/lib/validators.js.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Handshake, Plus, Search, Loader2, Phone, Building2,
  X, ChevronRight, Mail, User, Users, Briefcase,
} from 'lucide-react';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { emailValidationError } from '../../lib/validators';
import { useUrlTab } from '../../lib/hooks/useUrlState';

// ── helpers ──────────────────────────────────────────────────────────────
const STAGES = ['Planning', 'Lead Generation', 'Qualification', 'Proposal', 'Negotiation', 'Closed Won', 'Closed Lost'];
const stageTone = (s) => {
  const m = {
    Planning: 'bg-slate-100 text-slate-700 border-slate-200',
    'Lead Generation': 'bg-blue-100 text-blue-800 border-blue-200',
    Qualification: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    Proposal: 'bg-purple-100 text-purple-800 border-purple-200',
    Negotiation: 'bg-amber-100 text-amber-800 border-amber-200',
    'Closed Won': 'bg-emerald-100 text-emerald-800 border-emerald-200',
    'Closed Lost': 'bg-red-100 text-red-800 border-red-200',
  };
  return m[s] || 'bg-slate-100 text-slate-700 border-slate-200';
};
const inr = (n) => n == null ? '—' : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const TABS = ['Pipeline', 'Contacts', 'Companies', 'Consultants', 'Leads'];

// ── Bottom sheet shared wrapper ──────────────────────────────────────────
function BottomSheet({ onClose, title, children }) {
  return (
    <div className="fixed inset-0 z-30 bg-black/40 flex items-end" onClick={onClose}>
      <div className="bg-white w-full rounded-t-2xl p-5 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-display font-bold text-lg text-[#3b4872]">{title}</div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Input helper ─────────────────────────────────────────────────────────
const Inp = ({ label, value, onChange, type = 'text', placeholder, required }) => (
  <label className="block mb-3">
    <span className="text-xs font-medium text-slate-700">{label}{required && ' *'}</span>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
    />
  </label>
);

// ── SaveBtn ────────────────────────────────────────────────────────────
const SaveBtn = ({ saving, label, onClick }) => (
  <button
    onClick={onClick}
    disabled={saving}
    className="w-full py-3 bg-[#3b4872] hover:bg-[#2d3858] text-white font-display font-bold uppercase tracking-wider text-sm rounded-sm disabled:opacity-50"
  >
    {saving ? 'Saving…' : label}
  </button>
);

// ═══════════════════════════════════════════════════════════════════════
// PIPELINE TAB (existing, refactored)
// ═══════════════════════════════════════════════════════════════════════
function PipelineTab({ cache, setCache }) {
  const [loading, setLoading] = useState(!cache.length);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet('/crm/opportunities');
      const list = Array.isArray(data) ? data : (data?.opportunities || data?.items || []);
      setCache(Array.isArray(list) ? list : []);
    } catch { toast.error('Failed to load opportunities'); }
    finally { setLoading(false); }
  }, [setCache]);

  useEffect(() => { if (!cache.length) load(); }, []); // eslint-disable-line

  const filtered = cache.filter(r => {
    if (!q.trim()) return true;
    const n = q.toLowerCase();
    return (r.client || '').toLowerCase().includes(n) || (r.project_name || '').toLowerCase().includes(n);
  });

  const moveStage = async (opp, stage) => {
    try {
      await apiPut(`/crm/opportunities/${opp.id}`, { ...opp, stage });
      toast.success(`Moved to ${stage}`);
      setSelected(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <>
      <div className="px-3 pt-2 pb-1">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search project, company…"
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-sm text-sm" />
        </div>
      </div>
      <div className="px-3 pb-24 space-y-2 mt-1">
        {loading && <div className="text-center py-8 text-slate-500"><Loader2 className="animate-spin inline" /></div>}
        {!loading && filtered.length === 0 && <div className="text-center py-8 text-slate-500 text-sm">No opportunities.</div>}
        {!loading && filtered.map(r => (
          <button key={r.id} data-testid={`bd-row-${r.id}`} onClick={() => setSelected(r)}
            className="w-full text-left bg-white border border-slate-200 rounded-sm p-3 hover:shadow-sm active:scale-[0.99] transition-all">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{r.project_name || r.client}</div>
                <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><Building2 size={11} /> {r.client}</div>
              </div>
              <ChevronRight size={16} className="text-slate-400 flex-shrink-0" />
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded-sm ${stageTone(r.stage)}`}>{r.stage || 'Planning'}</span>
              <span className="text-sm font-mono font-bold text-[#3b4872]">{inr(r.estimated_value)}</span>
            </div>
          </button>
        ))}
      </div>

      {selected && (
        <BottomSheet title={selected.project_name || selected.client} onClose={() => setSelected(null)}>
          <div className="text-xs text-slate-500 mb-3">{selected.client} · {inr(selected.estimated_value)}</div>
          {selected.client_email && <div className="text-xs mb-2">📧 {selected.client_email}</div>}
          {selected.client_phone && (
            <a href={`tel:${selected.client_phone}`} className="text-xs text-[#3b4872] mb-3 flex items-center gap-1">
              <Phone size={12} /> {selected.client_phone}
            </a>
          )}
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Move to stage</div>
          <div className="grid grid-cols-2 gap-2">
            {STAGES.map(s => (
              <button key={s} onClick={() => moveStage(selected, s)} disabled={s === selected.stage}
                className={`py-2 px-2 text-xs font-bold uppercase tracking-wider border rounded-sm ${s === selected.stage ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-default' : `${stageTone(s)} hover:opacity-80`}`}>
                {s}
              </button>
            ))}
          </div>
        </BottomSheet>
      )}
      {showNew && <NewPipelineSheet onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
      <FloatingAdd onClick={() => setShowNew(true)} testid="bd-new" />
    </>
  );
}

function NewPipelineSheet({ onClose, onCreated }) {
  const [form, setForm] = useState({ client: '', project_name: '', client_email: '', client_phone: '', estimated_value: '', source: 'Direct', stage: 'Lead Generation', probability: 25 });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.client.trim()) return toast.error('Company is required');
    setSaving(true);
    try {
      await apiPost('/crm/opportunities', { ...form, estimated_value: form.estimated_value === '' ? null : Number(form.estimated_value), industry: 'Construction', location: '' });
      toast.success('Lead captured');
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <BottomSheet title="New Lead" onClose={onClose}>
      <Inp label="Company" value={form.client} onChange={e => setForm(f => ({ ...f, client: e.target.value }))} placeholder="e.g. Sapphire Pvt Ltd" required />
      <Inp label="Project Name" value={form.project_name} onChange={e => setForm(f => ({ ...f, project_name: e.target.value }))} placeholder="e.g. Tower B – Pune" />
      <Inp label="Contact Email" value={form.client_email} onChange={e => setForm(f => ({ ...f, client_email: e.target.value }))} placeholder="name@company.com" type="email" />
      <Inp label="Contact Phone" value={form.client_phone} onChange={e => setForm(f => ({ ...f, client_phone: e.target.value }))} placeholder="+91 …" />
      <Inp label="Estimated Value (₹)" value={form.estimated_value} onChange={e => setForm(f => ({ ...f, estimated_value: e.target.value }))} placeholder="5000000" type="number" />
      <SaveBtn saving={saving} label="Capture Lead" onClick={submit} />
    </BottomSheet>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// CONTACTS TAB
// ═══════════════════════════════════════════════════════════════════════
function ContactsTab({ cache, setCache }) {
  const [loading, setLoading] = useState(!cache.length);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiGet('/crm/contacts?scope=all');
      setCache(d.contacts || []);
    } catch { toast.error('Failed to load contacts'); }
    finally { setLoading(false); }
  }, [setCache]);

  useEffect(() => { if (!cache.length) load(); }, []); // eslint-disable-line

  const filtered = cache.filter(r => {
    if (!q.trim()) return true;
    const n = q.toLowerCase();
    return (r.name || '').toLowerCase().includes(n) || (r.company_name || '').toLowerCase().includes(n) || (r.phone || '').includes(n);
  });

  return (
    <>
      <SearchBar q={q} setQ={setQ} placeholder="Search name, company…" />
      <div className="px-3 pb-24 space-y-2 mt-1">
        {loading && <Spinner />}
        {!loading && filtered.length === 0 && <Empty label="No contacts." />}
        {!loading && filtered.map(r => (
          <button key={r.id} onClick={() => setSelected(r)} className="w-full text-left bg-white border border-slate-200 rounded-sm p-3 hover:shadow-sm transition-all">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-xs shrink-0">{(r.name||'?').slice(0,2).toUpperCase()}</div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{r.name}</div>
                <div className="text-xs text-slate-500 truncate">{r.designation || r.company_name || '—'}</div>
              </div>
              <ChevronRight size={16} className="text-slate-400 shrink-0" />
            </div>
          </button>
        ))}
      </div>
      {selected && (
        <BottomSheet title={selected.name} onClose={() => setSelected(null)}>
          {selected.designation && <div className="text-xs text-slate-500 mb-2">{selected.designation}</div>}
          {selected.company_name && <div className="text-xs text-slate-600 mb-2 flex items-center gap-1"><Building2 size={12}/>{selected.company_name}</div>}
          {selected.email && <a href={`mailto:${selected.email}`} className="text-xs text-[#3b4872] mb-2 flex items-center gap-1"><Mail size={12}/>{selected.email}</a>}
          {selected.phone && <a href={`tel:${selected.phone}`} className="text-xs text-[#3b4872] mb-2 flex items-center gap-1"><Phone size={12}/>{selected.phone}</a>}
          {selected.notes && <div className="text-xs text-slate-600 mt-2 border-t pt-2">{selected.notes}</div>}
        </BottomSheet>
      )}
      {showNew && <NewContactSheet onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
      <FloatingAdd onClick={() => setShowNew(true)} testid="contacts-new" />
    </>
  );
}

function NewContactSheet({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', designation: '', company_id: '' });
  const [companies, setCompanies] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { apiGet('/crm/companies').then(d => setCompanies(d.companies || [])).catch(() => {}); }, []);

  const submit = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    const emailErr = emailValidationError(form.email);
    if (emailErr) return toast.error(emailErr);
    setSaving(true);
    try {
      await apiPost('/crm/contacts', { ...form, tags: [], is_primary: false });
      toast.success('Contact added');
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <BottomSheet title="Add Contact" onClose={onClose}>
      <Inp label="Full Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Rahul Kulkarni" required />
      <Inp label="Email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="name@company.com" type="email" required />
      <Inp label="Phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+91 …" />
      <Inp label="Designation" value={form.designation} onChange={e => setForm(f => ({ ...f, designation: e.target.value }))} placeholder="e.g. Procurement Head" />
      <label className="block mb-3">
        <span className="text-xs font-medium text-slate-700">Company</span>
        <select value={form.company_id} onChange={e => setForm(f => ({ ...f, company_id: e.target.value }))} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
          <option value="">— None —</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </label>
      <SaveBtn saving={saving} label="Add Contact" onClick={submit} />
    </BottomSheet>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// COMPANIES TAB
// ═══════════════════════════════════════════════════════════════════════
function CompaniesTab({ cache, setCache }) {
  const [loading, setLoading] = useState(!cache.length);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiGet('/crm/companies?is_consultant=false');
      setCache(d.companies || []);
    } catch { toast.error('Failed to load companies'); }
    finally { setLoading(false); }
  }, [setCache]);

  useEffect(() => { if (!cache.length) load(); }, []); // eslint-disable-line

  const filtered = cache.filter(r => {
    if (!q.trim()) return true;
    const n = q.toLowerCase();
    return (r.name || '').toLowerCase().includes(n) || (r.industry || '').toLowerCase().includes(n) || (r.city || '').toLowerCase().includes(n);
  });

  return (
    <>
      <SearchBar q={q} setQ={setQ} placeholder="Search company, industry…" />
      <div className="px-3 pb-24 space-y-2 mt-1">
        {loading && <Spinner />}
        {!loading && filtered.length === 0 && <Empty label="No companies." />}
        {!loading && filtered.map(r => (
          <button key={r.id} onClick={() => setSelected(r)} className="w-full text-left bg-white border border-slate-200 rounded-sm p-3 hover:shadow-sm transition-all">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-sm bg-blue-100 text-blue-700 flex items-center justify-center shrink-0"><Building2 size={16} /></div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{r.name}</div>
                <div className="text-xs text-slate-500 truncate">{[r.industry, r.city].filter(Boolean).join(' · ') || '—'}</div>
              </div>
              <ChevronRight size={16} className="text-slate-400 shrink-0" />
            </div>
          </button>
        ))}
      </div>
      {selected && (
        <BottomSheet title={selected.name} onClose={() => setSelected(null)}>
          {selected.industry && <div className="text-xs text-slate-500 mb-1">{selected.industry}</div>}
          {selected.city && <div className="text-xs text-slate-600 mb-1">{[selected.city, selected.state].filter(Boolean).join(', ')}</div>}
          {selected.website && <a href={selected.website} target="_blank" rel="noreferrer" className="text-xs text-[#3b4872] underline mb-1 block">{selected.website}</a>}
          {selected.notes && <div className="text-xs text-slate-600 mt-2 border-t pt-2">{selected.notes}</div>}
        </BottomSheet>
      )}
      {showNew && <NewCompanySheet onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
      <FloatingAdd onClick={() => setShowNew(true)} testid="companies-new" />
    </>
  );
}

function NewCompanySheet({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', industry: '', city: '', website: '' });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.name.trim()) return toast.error('Company name is required');
    setSaving(true);
    try {
      await apiPost('/crm/companies', { ...form, is_consultant: false, record_type: 'bd_company', country: 'India', tags: [] });
      toast.success('Company added');
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <BottomSheet title="Add Company" onClose={onClose}>
      <Inp label="Company Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Lodha Developers" required />
      <Inp label="Industry" value={form.industry} onChange={e => setForm(f => ({ ...f, industry: e.target.value }))} placeholder="e.g. Real Estate" />
      <Inp label="City" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder="e.g. Pune" />
      <Inp label="Website" value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} placeholder="https://…" type="url" />
      <SaveBtn saving={saving} label="Add Company" onClick={submit} />
    </BottomSheet>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// CONSULTANTS TAB
// ═══════════════════════════════════════════════════════════════════════
function ConsultantsTab({ cache, setCache }) {
  const [loading, setLoading] = useState(!cache.length);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiGet('/crm/companies?is_consultant=true');
      setCache(d.companies || []);
    } catch { toast.error('Failed to load consultants'); }
    finally { setLoading(false); }
  }, [setCache]);

  useEffect(() => { if (!cache.length) load(); }, []); // eslint-disable-line

  const filtered = cache.filter(r => {
    if (!q.trim()) return true;
    const n = q.toLowerCase();
    return (r.name || '').toLowerCase().includes(n) || (r.consultant_type || '').toLowerCase().includes(n);
  });

  return (
    <>
      <SearchBar q={q} setQ={setQ} placeholder="Search consultant, type…" />
      <div className="px-3 pb-24 space-y-2 mt-1">
        {loading && <Spinner />}
        {!loading && filtered.length === 0 && <Empty label="No consultants." />}
        {!loading && filtered.map(r => (
          <button key={r.id} onClick={() => setSelected(r)} className="w-full text-left bg-white border border-slate-200 rounded-sm p-3 hover:shadow-sm transition-all">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-sm bg-violet-100 text-violet-700 flex items-center justify-center shrink-0"><Users size={16} /></div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{r.name}</div>
                <div className="text-xs text-slate-500 truncate">{r.consultant_type || '—'}{r.city ? ` · ${r.city}` : ''}</div>
              </div>
              <ChevronRight size={16} className="text-slate-400 shrink-0" />
            </div>
          </button>
        ))}
      </div>
      {selected && (
        <BottomSheet title={selected.name} onClose={() => setSelected(null)}>
          {selected.consultant_type && <div className="text-xs font-bold text-violet-700 mb-2">{selected.consultant_type}</div>}
          {selected.city && <div className="text-xs text-slate-600 mb-1">{selected.city}</div>}
          {selected.website && <a href={selected.website} target="_blank" rel="noreferrer" className="text-xs text-[#3b4872] underline mb-1 block">{selected.website}</a>}
          {selected.notes && <div className="text-xs text-slate-600 mt-2 border-t pt-2">{selected.notes}</div>}
        </BottomSheet>
      )}
      {showNew && <NewConsultantSheet onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
      <FloatingAdd onClick={() => setShowNew(true)} testid="consultants-new" />
    </>
  );
}

function NewConsultantSheet({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', consultant_type: 'PMC', city: '' });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      await apiPost('/crm/companies', { ...form, is_consultant: true, record_type: 'bd_company', country: 'India', tags: [] });
      toast.success('Consultant added');
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <BottomSheet title="Add Consultant" onClose={onClose}>
      <Inp label="Firm Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. XYZ Architects" required />
      <label className="block mb-3">
        <span className="text-xs font-medium text-slate-700">Consultant Type</span>
        <select value={form.consultant_type} onChange={e => setForm(f => ({ ...f, consultant_type: e.target.value }))} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
          {['PMC', 'Cost Consultant', 'Structural Consultant', 'Architect'].map(t => <option key={t}>{t}</option>)}
        </select>
      </label>
      <Inp label="City" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder="e.g. Mumbai" />
      <SaveBtn saving={saving} label="Add Consultant" onClick={submit} />
    </BottomSheet>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// LEADS TAB
// ═══════════════════════════════════════════════════════════════════════
function LeadsTab({ cache, setCache }) {
  const [loading, setLoading] = useState(!cache.length);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiGet('/crm/leads');
      setCache(d.leads || []);
    } catch { toast.error('Failed to load leads'); }
    finally { setLoading(false); }
  }, [setCache]);

  useEffect(() => { if (!cache.length) load(); }, []); // eslint-disable-line

  const filtered = cache.filter(r => {
    if (!q.trim()) return true;
    const n = q.toLowerCase();
    return (r.client || '').toLowerCase().includes(n) || (r.contact_person || '').toLowerCase().includes(n);
  });

  const stageColor = (s) => {
    const m = { 'New': 'text-blue-700', 'Contacted': 'text-indigo-700', 'Qualified': 'text-emerald-700', 'Lost': 'text-red-600' };
    return m[s] || 'text-slate-600';
  };

  return (
    <>
      <SearchBar q={q} setQ={setQ} placeholder="Search client, contact…" />
      <div className="px-3 pb-24 space-y-2 mt-1">
        {loading && <Spinner />}
        {!loading && filtered.length === 0 && <Empty label="No leads." />}
        {!loading && filtered.map(r => (
          <button key={r.id} onClick={() => setSelected(r)} className="w-full text-left bg-white border border-slate-200 rounded-sm p-3 hover:shadow-sm transition-all">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{r.client}</div>
                <div className="text-xs text-slate-500 truncate">{r.contact_person || r.project_type || '—'}</div>
              </div>
              <span className={`text-[10px] font-bold uppercase shrink-0 ${stageColor(r.stage)}`}>{r.stage}</span>
            </div>
          </button>
        ))}
      </div>
      {selected && (
        <BottomSheet title={selected.client} onClose={() => setSelected(null)}>
          {selected.contact_person && <div className="text-xs text-slate-600 mb-1 flex items-center gap-1"><User size={12}/>{selected.contact_person}</div>}
          {selected.email && <a href={`mailto:${selected.email}`} className="text-xs text-[#3b4872] mb-1 flex items-center gap-1"><Mail size={12}/>{selected.email}</a>}
          {selected.phone && <a href={`tel:${selected.phone}`} className="text-xs text-[#3b4872] mb-2 flex items-center gap-1"><Phone size={12}/>{selected.phone}</a>}
          {selected.project_type && <div className="text-xs text-slate-500 mb-1">Type: {selected.project_type}</div>}
          {selected.source && <div className="text-xs text-slate-500 mb-1">Source: {selected.source}</div>}
          <div className={`text-xs font-bold uppercase mt-1 ${stageColor(selected.stage)}`}>{selected.stage}</div>
          {selected.notes && <div className="text-xs text-slate-600 mt-2 border-t pt-2">{selected.notes}</div>}
        </BottomSheet>
      )}
      {showNew && <NewLeadSheet onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
      <FloatingAdd onClick={() => setShowNew(true)} testid="leads-new" />
    </>
  );
}

function NewLeadSheet({ onClose, onCreated }) {
  const [form, setForm] = useState({ client: '', contact_person: '', email: '', phone: '', project_type: 'EPC', source: 'Direct', stage: 'New', priority: 'Medium', owner: '' });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.client.trim()) return toast.error('Client company is required');
    if (form.email && emailValidationError(form.email)) return toast.error(emailValidationError(form.email));
    setSaving(true);
    try {
      await apiPost('/crm/leads', { ...form });
      toast.success('Lead added');
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <BottomSheet title="Add Lead" onClose={onClose}>
      <Inp label="Client Company" value={form.client} onChange={e => setForm(f => ({ ...f, client: e.target.value }))} placeholder="e.g. Adani Group" required />
      <Inp label="Contact Person" value={form.contact_person} onChange={e => setForm(f => ({ ...f, contact_person: e.target.value }))} placeholder="Name of contact" />
      <Inp label="Email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="name@company.com" type="email" />
      <Inp label="Phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+91 …" />
      <label className="block mb-3">
        <span className="text-xs font-medium text-slate-700">Project Type</span>
        <select value={form.project_type} onChange={e => setForm(f => ({ ...f, project_type: e.target.value }))} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
          {['EPC', 'Civil', 'MEP', 'Industrial', 'Infrastructure', 'Other'].map(t => <option key={t}>{t}</option>)}
        </select>
      </label>
      <label className="block mb-3">
        <span className="text-xs font-medium text-slate-700">Source</span>
        <select value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
          {['Direct', 'Referral', 'LinkedIn', 'Exhibition', 'Website', 'Other'].map(t => <option key={t}>{t}</option>)}
        </select>
      </label>
      <SaveBtn saving={saving} label="Add Lead" onClick={submit} />
    </BottomSheet>
  );
}

// ── Shared micro-components ───────────────────────────────────────────
const Spinner = () => <div className="text-center py-8 text-slate-500"><Loader2 className="animate-spin inline" /></div>;
const Empty = ({ label }) => <div className="text-center py-8 text-slate-500 text-sm">{label}</div>;
const SearchBar = ({ q, setQ, placeholder }) => (
  <div className="px-3 pt-2 pb-1">
    <div className="relative">
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input value={q} onChange={e => setQ(e.target.value)} placeholder={placeholder}
        className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-sm text-sm" />
    </div>
  </div>
);
const FloatingAdd = ({ onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className="fixed bottom-24 right-4 z-20 w-12 h-12 bg-[#3b4872] text-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#2d3858]">
    <Plus size={22} />
  </button>
);

// ═══════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════
const TAB_ICONS = {
  Pipeline: Handshake,
  Contacts: User,
  Companies: Building2,
  Consultants: Users,
  Leads: Briefcase,
};

export default function MobileBD() {
  const [tab, setTab] = useUrlTab('tab', 'Pipeline');
  const [caches, setCaches] = useState({ Pipeline: [], Contacts: [], Companies: [], Consultants: [], Leads: [] });
  const setCache = (key) => (val) => setCaches(c => ({ ...c, [key]: val }));

  const tabCount = (key) => caches[key].length || '';

  return (
    <div data-testid="mobile-bd" className="min-h-screen bg-slate-50 pb-4">
      {/* Header */}
      <header className="bg-[#3b4872] text-white px-4 py-3 sticky top-0 z-10 flex items-center gap-3">
        <Link to="/m" className="p-1 hover:bg-white/10 rounded-sm"><ArrowLeft size={20} /></Link>
        <Handshake size={20} />
        <div className="flex-1">
          <div className="font-display font-bold text-base">Business Development</div>
          <div className="text-[10px] text-white/70">{tab}</div>
        </div>
      </header>

      {/* Tab bar */}
      <div className="bg-white border-b border-slate-200 sticky top-[52px] z-[5]">
        <div className="flex overflow-x-auto scrollbar-hide">
          {TABS.map(t => {
            const Icon = TAB_ICONS[t];
            return (
              <button
                key={t}
                onClick={() => setTab(t)}
                data-testid={`bd-tab-${t.toLowerCase()}`}
                className={`flex items-center gap-1.5 px-4 py-3 text-xs font-display font-bold uppercase tracking-wider whitespace-nowrap border-b-2 transition-colors ${
                  tab === t
                    ? 'border-[#3b4872] text-[#3b4872]'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <Icon size={12} />
                {t}
                {tabCount(t) ? <span className="ml-0.5 text-[9px] text-slate-400">({tabCount(t)})</span> : null}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab panels */}
      <div className="relative">
        {tab === 'Pipeline'    && <PipelineTab    cache={caches.Pipeline}    setCache={setCache('Pipeline')} />}
        {tab === 'Contacts'    && <ContactsTab    cache={caches.Contacts}    setCache={setCache('Contacts')} />}
        {tab === 'Companies'   && <CompaniesTab   cache={caches.Companies}   setCache={setCache('Companies')} />}
        {tab === 'Consultants' && <ConsultantsTab cache={caches.Consultants} setCache={setCache('Consultants')} />}
        {tab === 'Leads'       && <LeadsTab       cache={caches.Leads}       setCache={setCache('Leads')} />}
      </div>
    </div>
  );
}
